import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    BundleTypes,
    generateRandomUniqueId,
    generateSwatchConfigsFromProductMap,
    updateBundleConfigWithRealTimeProducts,
} from "../constants";
import styles from "./BundleBuilder.module.css";
import BundleBuilderContentForm from "./BundleBuilderContentForm/BundleBuilderContentForm";
import BundleBuilderDesignForm from "./BundleBuilderDesignForm/BundleBuilderDesignForm";
import BundleBuilderTargetForm from "./BundleBuilderTargetForm/BundleBuilderTargetForm";
import {
    DEFAULT_FIELD,
    DEFAULT_FIXED_BOX,
    DEFAULT_PRODUCT_CONDITION,
    DEFAULT_SUB_STEP,
    initialConfigData,
} from "./hooks/useBundleBuilderHook";
import { useBundleEditor, SKIP_KEYS } from "./hooks/useBundleEditor";
import { BundleBuilderFormPreviewWrapper } from "./BundleBuilderFormPreviewWrapper/BundleBuilderFormPreviewWrapper";
import { FormProvider, useWatch } from "react-hook-form";
import { VariantTabs } from "../../../components/VariantEditor/VariantTabs";
import { VariantFieldContext } from "../../../components/FormFields";
import { endPoints, getData, postData } from "../../../utilities/networkCalls";
import { createContext, Dispatch, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getBundleRoute, migrateBBConfig } from "../../constants";
import { BundleBuilderConfigInterface, ProductConditions, StepsType } from "../Types";
import { useStableDeepValue } from "../../../hooks/useStableDeepValue";
import { AppConfigContext } from "../../../context/AppConfigContext";
import { getUpdatedTargeting } from "../../shouldRenderTargeting";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import { BundleBuilderPreview } from "./BundleBuilderBundle/BundleBuilderBundle";
import { scrollToTop, getBoolean } from "../../../utilities/functions";
import {
    deepMergeWithDefaults,
    normalizeDiscountOptions,
    normalizeFixedBoxes,
    normalizeStepConditions,
} from "../../utilities";
import { useInspectorMode } from "../../../hooks/useInspectorMode";
import TemplateSelectionScreenBB from "./TemplateSelectionScreenBB/TemplateSelectionScreenBB";
import { isAbTestingEnabledPlan } from "../../../components/Plans/plansConstants";
import StopABTestModal from "../../ABTest/StopABTestModal";
import { Link } from "@shopify/polaris";
import { nanoid } from "nanoid";

export interface BundleBuilderContextType {
    handleNext: (step: number, stepName: string) => void;
    isMobile: boolean;
    SwatchesBundleConfigTouchedRef: React.MutableRefObject<Record<string, string>>;
    // --- Inspector mode additions ---
    inspectorMode?: boolean;
    setInspectorMode?: Dispatch<React.SetStateAction<boolean>>;
    inspectorTarget: any | null;
}

export const BundleBuilderContext = createContext<BundleBuilderContextType>(null as any);

const BundleBuilder = () => {
    const location = useLocation();
    const { footer_layout } = (location.state || {}) as { footer_layout?: string };

    const SwatchesBundleConfigTouchedRef = useRef<Record<string, string>>({});

    const editorInitialValues = useMemo(
        () => (footer_layout ? { ...initialConfigData, footer_layout: footer_layout as any } : initialConfigData),
        [footer_layout],
    );

    const editor = useBundleEditor({
        initialValues: editorInitialValues,
        onSave: async () => {},
    });

    const { trackFromTabs, trackCtaClick } = useBundleMixpanelHook();

    // Reactive watches — only subscribe to the fields actually needed for
    // conditional rendering / side-effects in this component.
    const formId = useWatch({ control: editor.form.control, name: "form_id" });
    const selectedTemplateBb = useWatch({ control: editor.form.control, name: "selected_template_bb" });
    const footerLayoutWatch = useWatch({ control: editor.form.control, name: "footer_layout" });
    // Full form watch — drives live preview + storefront widget so every field
    // change is reflected immediately, and every userShopname-based conditional
    // downstream in BundleBuilderBundle.tsx keeps receiving a complete config.
    const liveFormValues = useWatch({ control: editor.form.control }) as any as BundleBuilderConfigInterface;

    const [selected, setSelected] = useState(0);
    const [fetchingBundle, setFetchingBundle] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const { bId } = useParams();
    const goTo = useNavigate();
    const appConfigContext = useContext(AppConfigContext);
    const { sft, globalSettingsInfo } = appConfigContext;
    const { globalSettings, loading } = globalSettingsInfo;

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    useEffect(() => {
        if (!loading) {
            if (bId) {
                fetchBundle(bId);
            } else {
                setFetchingBundle(false);
                const currentValues = editor.form.getValues();
                const newEmbedId = generateRandomUniqueId();

                const newConfig = {
                    bundles_design: {
                        ...currentValues?.bundles_design,
                        ...globalSettings?.design_settings_bundle_builder,
                    } as BundleBuilderConfigInterface["bundles_design"],
                    general_settings: {
                        ...currentValues?.general_settings,
                        ...(globalSettings?.hasOwnProperty("general_settings")
                            ? {
                                  ...globalSettings?.general_settings,
                                  show_powered_by_kefi: globalSettings?.general_settings?.hasOwnProperty(
                                      "show_powered_by_kefi",
                                  )
                                      ? globalSettings?.general_settings?.show_powered_by_kefi
                                      : true,
                              }
                            : {}),
                    } as unknown as BundleBuilderConfigInterface["general_settings"],
                    bundles_settings: {
                        ...currentValues?.bundles_settings,
                        ...globalSettings?.bundle_settings_bundle_builder,
                    } as BundleBuilderConfigInterface["bundles_settings"],
                    bundle_embed_id: newEmbedId,
                };

                editor.form.reset({ ...currentValues, ...newConfig });
            }
        }
    }, [globalSettings, loading, bId]);

    useEffect(() => {
        if (formId) {
            goTo(getBundleRoute(BundleTypes.BUNDLE_BUILDER, formId));
        }
    }, [formId]);

    const fetchBundle = async (id: string | null = "") => {
        if (!id) {
            shopify.saveBar.hide("bundle-save-bar");
            goTo(getBundleRoute(BundleTypes.BUNDLE_BUILDER));
        }
        setFetchingBundle(true);
        const res = await getData({ url: endPoints.getBundle + id });
        if (res.status == 200) {
            if (res?.data?.bundle?.form_id) {
                const migratedBundle = migrateBBConfig(res.data.bundle);
                const newConfig = await updateBundleConfigWithRealTimeProducts(sft, [migratedBundle]);
                const currentValues = editor.form.getValues();
                const normalizedDiscountOptions = migratedBundle?.discount_options?.length
                    ? deepMergeWithDefaults(
                          currentValues.discount_options,
                          normalizeDiscountOptions(migratedBundle.discount_options),
                      )
                    : undefined;
                const normalizedFixedBoxes = migratedBundle?.fixed_boxes?.length
                    ? deepMergeWithDefaults([DEFAULT_FIXED_BOX], normalizeFixedBoxes(migratedBundle.fixed_boxes))
                    : undefined;
                const _c: BundleBuilderConfigInterface & { publish_count?: number } = {
                    ...deepMergeWithDefaults(currentValues, migratedBundle),
                    ...(normalizedDiscountOptions ? { discount_options: normalizedDiscountOptions } : {}),
                    ...(normalizedFixedBoxes ? { fixed_boxes: normalizedFixedBoxes } : {}),
                    products: newConfig[0].products,
                    publish_count: res.data.publish_count,
                    footer_layout: res?.data?.bundle?.footer_layout || "bottom",
                    selected_template_bb: res?.data?.bundle?.selected_template_bb || "full_card_info",
                    targeting: res?.data?.bundle?.targeting,
                    product_conditions: res?.data?.bundle?.product_conditions?.length
                        ? res?.data?.bundle?.product_conditions?.map((pc: ProductConditions) => ({
                              ...DEFAULT_PRODUCT_CONDITION,
                              ...pc,
                          }))
                        : [],
                    review_step: {
                        ...res?.data?.bundle?.review_step,
                        enable_addon_products: res?.data?.bundle?.review_step?.enable_addon_products ?? false,
                        addon_section_title: res?.data?.bundle?.review_step?.addon_section_title ?? "Complete your box",
                        addon_button_text: res?.data?.bundle?.review_step?.addon_button_text ?? "Add",
                        addon_product_discount_label:
                            res?.data?.bundle?.review_step?.addon_product_discount_label ?? "Add-ons",
                        addon_products_total_lable:
                            res?.data?.bundle?.review_step?.addon_products_total_lable ?? "Add-ons discount",
                        addon_product_selection: res?.data?.bundle?.review_step?.addon_product_selection ?? "specific",
                        addon_products: res?.data?.bundle?.review_step?.addon_products ?? [],
                        collections: res?.data?.bundle?.review_step?.collections ?? [],
                        apply_discount_on_addon_products:
                            res?.data?.bundle?.review_step?.apply_discount_on_addon_products ?? false,
                        discount_type: res?.data?.bundle?.review_step?.discount_type ?? "percentage",
                        discount_value: res?.data?.bundle?.review_step?.discount_value ?? 10,
                    },
                };
                _c.bundles_design = {
                    ...currentValues?.bundles_design,
                    ..._c.bundles_design,
                    total_footer_price_color:
                        _c?.bundles_design?.total_footer_price_color || _c?.bundles_design?.total_footer_text_color,
                    total_footer_strike_off_price_color:
                        _c?.bundles_design?.total_footer_strike_off_price_color ||
                        _c?.bundles_design?.product_price_color,
                    total_footer_discount_color:
                        _c?.bundles_design?.total_footer_discount_color || _c?.bundles_design?.total_footer_text_color,
                    product_custom_img_size_mobile:
                        _c?.bundles_design?.product_custom_img_size_mobile ??
                        _c?.bundles_design?.product_custom_img_size,
                };
                if (!_c.tier_discount_basis) {
                    _c.tier_discount_basis = "items_count";
                }
                if (_c.steps?.length) {
                    _c.steps = _c?.steps.map((step: StepsType) => ({
                        ...step,
                        step_conditions: normalizeStepConditions(step),
                        show_step_conditions: step.hasOwnProperty("show_step_conditions")
                            ? step.show_step_conditions
                            : true,
                        substep_rules_type: step.substep_rules_type || "substep_level",
                        subSteps: step?.subSteps?.length
                            ? step?.subSteps?.map((subStep) => ({ ...DEFAULT_SUB_STEP, ...subStep }))
                            : [],
                        fields: step?.fields?.length
                            ? step?.fields?.map((field) => ({ ...DEFAULT_FIELD, ...field }))
                            : [],
                    }));
                }

                _c.general_settings = {
                    ...currentValues?.general_settings,
                    ..._c.general_settings,
                    show_powered_by_kefi: _c?.general_settings?.hasOwnProperty("show_powered_by_kefi")
                        ? _c?.general_settings?.show_powered_by_kefi
                        : true,
                } as unknown as BundleBuilderConfigInterface["general_settings"];

                _c.targeting = { ..._c.targeting, ...getUpdatedTargeting(_c.targeting) };

                // initializeEditor rebuilds the full variant cache from ab_config
                // (for A/B bundles) OR just sets the base (for regular bundles).
                editor.initializeEditor(_c);
                setFetchingBundle(false);
            } else {
                shopify.saveBar.hide("bundle-save-bar");
                goTo(getBundleRoute(BundleTypes.BUNDLE_BUILDER));
            }
        } else {
            shopify.saveBar.hide("bundle-save-bar");
            goTo(getBundleRoute(BundleTypes.BUNDLE_BUILDER));
        }
    };

    const handleWinnerApplied = async (bundle: any) => {
        const currentValues = editor.form.getValues() as any;
        const [enriched] = await updateBundleConfigWithRealTimeProducts(sft, [bundle]);
        editor.applyWinnerBundle({
            ...currentValues,
            ...enriched,
            status: currentValues.status,
            shop: currentValues.shop,
            bundle_embed_id: currentValues.bundle_embed_id,
            user_id: currentValues.user_id,
            product_bundle_id: currentValues.product_bundle_id,
        } as any as BundleBuilderConfigInterface);
    };

    const handleNext = async (num: number, screen = "-") => {
        const valid = await editor.form.trigger();
        if (valid) {
            trackFromTabs({
                cta: "Form Tabs Switched",
                tabName: screen,
                bundleType: editor.form.getValues("bundle_type"),
            });
            setSelected(num);
        } else {
            scrollToTop();
        }
    };

    // --- Inspector mode -----
    const [inspectorMode, setInspectorMode] = useState(false);
    const hoveredElRef = useRef<HTMLElement | null>(null);
    const [selectedEl, setSelectedEl] = useState<HTMLElement | null>(null);
    const [inspectorTarget, setInspectorTarget] = useState<any>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const { handleInspectorClick } = useInspectorMode({
        inspectorMode,
        previewRef,
        selectedEl,
        setSelectedEl,
        hoveredElRef,
        setInspectorTarget,
        handleNext,
        // Pass a live getter so the inspector always sees the latest form values
        bundleConfig: editor.form.getValues() as any,
    });

    // Plan info
    const { plansInfo } = appConfigContext;
    const { isExhausted, isTrial, currentPlan } = plansInfo;
    const abTestingLocked = !isAbTestingEnabledPlan(currentPlan?.plan_id);
    const [activatingBundle, setActivatingBundle] = useState(false);
    const [savingBundleLocal, setSavingBundleLocal] = useState(false);
    const [stopAbModalOpen, setStopAbModalOpen] = useState(false);

    const lockedTooltipContent = abTestingLocked ? (
        <span>
            Available on a higher plan.{" "}
            <Link
                onClick={() => {
                    if (editor.form.formState.isDirty || editor.hasUnsavedChanges) {
                        shopify.saveBar.leaveConfirmation();
                        return;
                    }
                    goTo("/billing");
                }}
            >
                Upgrade Plan
            </Link>
        </span>
    ) : (
        ""
    );

    // Stopping an AB setup that was never saved is purely local (no experiment
    // exists server-side yet). Stopping a persisted one requires picking a
    // winner — see StopABTestModal, which mirrors ABTestDashboard's flow.
    const onStopAB = () => {
        if (abTestingLocked) return;
        if (!editor.abEnabled) return;
        trackCtaClick({ cta: "Stop A/B Testing", ctaType: "Click", bundleType: BundleTypes.BUNDLE_BUILDER });
        if (editor.savedAbEnabled) {
            setStopAbModalOpen(true);
        } else {
            editor.disableAB();
        }
    };

    const onEnableAB = () => {
        if (abTestingLocked) return;
        trackCtaClick({ cta: "Enable A/B Testing", ctaType: "Click", bundleType: BundleTypes.BUNDLE_BUILDER });
        editor.enableAB();
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Save — RHF validates, builds payload, calls API
    // ─────────────────────────────────────────────────────────────────────────
    const handleSaveBundle = async () => {
        const valid = await editor.form.trigger();
        if (!valid) {
            scrollToTop();
            return;
        }

        if (editor.abEnabled && editor.variants.length > 0) {
            const invalidTarget = await editor.validateAllTargets();
            if (invalidTarget) {
                editor.jumpToTarget(invalidTarget);
                await editor.form.trigger();
                scrollToTop();
                shopify.toast.show("Please fix the errors in all variants before saving.", {
                    isError: true,
                    duration: 3000,
                });
                return;
            }
        }

        setSavingBundleLocal(true);
        const rhfValues = editor.form.getValues() as any;

        if (!rhfValues?.form_id) {
            setInspectorMode(false);
        }

        let payload: BundleBuilderConfigInterface;

        if (editor.abEnabled && editor.variants.length > 0) {
            // IMPORTANT: when AB testing is active, `rhfValues` is the
            // currently-ACTIVE VARIANT's fully-merged doc (base + that
            // variant's overrides), not the hidden base document. Using it for
            // the top-level payload fields would leak whatever the active
            // variant currently shows into the base itself.
            // `editor.buildPayload()` already computes the correct hidden base
            // + properly-diffed per-variant overrides — use it wholesale.
            payload = editor.buildPayload();
        } else {
            payload = { ...rhfValues };
            delete (payload as any).ab_config;
        }

        const res = await postData({
            url: (payload as any)?.form_id ? endPoints.updateBundle : endPoints.saveBundle,
            payload: { ...payload },
        });

        if (res.status === 200 || res.status === 201) {
            const patches: Record<string, any> = {};
            if (res?.data?.hasOwnProperty("product_handle") && res?.data?.hasOwnProperty("product_bundle_id")) {
                patches.bundle_as_product = {
                    ...(rhfValues.bundle_as_product || {}),
                    product_handle: res?.data.product_handle ?? "",
                };
                patches.product_bundle_id = res?.data?.product_bundle_id;
            }
            if (!rhfValues?.form_id) {
                patches.form_id = res.data.data;
            }
            Object.entries(patches).forEach(([key, val]) => editor.patchAllTargets(key as any, val));

            shopify.saveBar.hide("bundle-save-bar");
            goTo(getBundleRoute(BundleTypes.BUNDLE_BUILDER, res.data.data || rhfValues?.form_id));
            shopify.toast.show("Data saved successfully.", { isError: false, duration: 1500 });

            // Swatch metafield sync stays out-of-band, same as before — synced
            // after save completes, independent of the AB variant cache.
            const changedSwatches = generateSwatchConfigsFromProductMap((payload as any)?.swatchesProductsMap);
            if (changedSwatches.length > 0) {
                const grouped: Record<string, any[]> = {};
                changedSwatches.forEach((item) => {
                    if (!grouped[item.productId]) grouped[item.productId] = [];
                    grouped[item.productId].push(item);
                });
                await postData({
                    url: endPoints.updateSwatchesMeta,
                    payload: { kefi_swatches: Object.values(grouped).map((group) => ({ options: group })) },
                });
            }

            // Refreshes the saved configuration baseline in useBundleEditor.
            // Must be the actual base+ab_config document that was just
            // persisted (`payload`), NOT `editor.form.getValues()`.
            editor.updateLastSavedConfig({
                ...payload,
                ...patches,
                form_id: res.data.data || (payload as any).form_id,
            } as any);
        } else if (
            res?.status === 400 &&
            res?.data?.message &&
            res?.data?.message.includes("Handle") &&
            res?.data?.message.includes("is already in use by another page")
        ) {
            shopify.toast.show(res?.data?.message, { isError: true, duration: 8000 });
        } else {
            shopify.toast.show("Failed to save data. Please try again.", { isError: true, duration: 2000 });
        }

        setSavingBundleLocal(false);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Discard — reset RHF to last saved config
    // ─────────────────────────────────────────────────────────────────────────
    const handleDiscardBundle = () => {
        editor.resetCache();
        shopify.saveBar.hide("bundle-save-bar");
        setSelected(0);
    };

    const handleActivate = async (currentStatus: boolean) => {
        if (!isTrial && isExhausted && !currentStatus) return;
        const currentFormId = editor.form.getValues("form_id");
        if (!currentFormId) return;
        setActivatingBundle(true);
        try {
            if (!currentStatus) {
                const isExtensionEnabled = await getData({ url: endPoints.extensionStatus });
                if (isExtensionEnabled.data.status === 0) return;
            }
            const res = await postData({
                url: endPoints.updateBundleStatus,
                payload: { form_id: currentFormId, status: !currentStatus, delete: false },
            });
            if (res.status === 200 || res.status === 201) {
                editor.patchAllTargets("status", !currentStatus);
                editor.patchAllTargets("bundle_as_product", (prev: any) => ({
                    ...prev,
                    product_status: currentStatus ? "DRAFT" : "ACTIVE",
                }));
                shopify.toast.show("Bundle status updated successfully!", { isError: false });
            } else {
                shopify.toast.show("Failed to update bundle status. Please try again.", { isError: true });
            }
        } catch {
            shopify.toast.show("An error occurred while updating bundle status.", { isError: true });
        } finally {
            setActivatingBundle(false);
        }
    };

    const handleDuplicate = async (name: string) => {
        if (!name) return;
        // Same base-vs-active-variant hazard as handleSaveBundle: while AB
        // testing is active, editor.form.getValues() is the active variant's
        // merged view, not the hidden base — go through buildPayload() for the
        // correct base. A duplicate must stay an independent experiment, so
        // ab_config is carried over but re-keyed with a fresh exp_id and fresh
        // variant ids (traffic_percentage is keyed by variant id, so it's
        // remapped alongside) — otherwise the duplicate would share the
        // original's exp_id and visitor/click stats would get attributed
        // across both bundles.
        const currentValues =
            editor.abEnabled && editor.variants.length > 0
                ? (editor.buildPayload() as any)
                : (editor.form.getValues() as any);
        const duplicatePayload = { ...currentValues };
        if (duplicatePayload?.ab_config?.enable) {
            const idMap = new Map<string, string>();
            const variants = (duplicatePayload.ab_config.variants || []).map((variant: any) => {
                const newId = nanoid();
                idMap.set(variant.id, newId);
                return { ...variant, id: newId };
            });
            const traffic_percentage = Object.fromEntries(
                Object.entries(duplicatePayload.ab_config.traffic_percentage || {}).map(([id, pct]) => [
                    idMap.get(id) ?? id,
                    pct,
                ]),
            );
            duplicatePayload.ab_config = {
                ...duplicatePayload.ab_config,
                exp_id: nanoid(),
                variants,
                traffic_percentage,
            };
        }
        const res = await postData({
            url: endPoints.saveBundle,
            payload: {
                ...duplicatePayload,
                title: name,
                offer_name: "",
                bundle_embed_id: generateRandomUniqueId(),
            },
        });
        if (res.status === 200) {
            shopify.toast.show("Bundle duplicated successfully", { isError: false });
            if (res?.data?.data) goTo(getBundleRoute(BundleTypes.BUNDLE_BUILDER, res.data.data));
        } else {
            shopify.toast.show("Error duplicating bundle", { isError: true });
        }
    };

    const getSnapshot = () => editor.form.getValues() as any as BundleBuilderConfigInterface;

    // liveFormValues comes from a full-form useWatch(), which deep-clones the
    // whole form on every keystroke — so `steps`/`review_step`/etc. get a new
    // reference even when only an unrelated field (e.g. title) changed. That
    // instability was tripping BundleBuilderBundle.tsx's internal effects
    // (keyed off bundleConfig?.steps/review_step/product_conditions) into
    // re-deriving and re-rendering the whole preview, including reloading
    // every step's products, on every keystroke. Stabilizing here keeps
    // unchanged branches referentially identical across renders so those
    // effects only re-run when their data actually changed.
    const previewBundleConfig = useStableDeepValue({
        ...liveFormValues,
        SwatchesBundleConfigTouchedRef: SwatchesBundleConfigTouchedRef,
        fixed_boxes: [...(liveFormValues?.fixed_boxes || [])].sort((a, b) => a.itemsCount - b.itemsCount),
    });

    return (
        <BundleBuilderContext.Provider
            value={{
                handleNext,
                isMobile,
                SwatchesBundleConfigTouchedRef,
                // --- inspector mode additions ---
                inspectorMode,
                setInspectorMode,
                inspectorTarget,
            }}
        >
            {/* FormProvider: single RHF instance wraps the entire editor tree */}
            <FormProvider {...editor.form}>
                <VariantFieldContext.Provider
                    value={{
                        activeTarget: editor.activeTarget,
                        variants: editor.variants,
                        abEnabled: editor.abEnabled,
                        skipKeys: SKIP_KEYS,
                    }}
                >
                    {loading || fetchingBundle || (footerLayoutWatch && selectedTemplateBb) || formId ? (
                        <BundleBuilderFormPreviewWrapper
                            bundleConfig={getSnapshot()}
                            loading={fetchingBundle || loading}
                            savingBundle={savingBundleLocal}
                            setSavingBundle={setSavingBundleLocal}
                            activatingBundle={activatingBundle}
                            onSave={handleSaveBundle}
                            onDiscard={handleDiscardBundle}
                            onActivate={handleActivate}
                            onDuplicate={handleDuplicate}
                            selected={selected}
                            setSelected={setSelected}
                            handleNext={handleNext}
                            onEnableAB={onEnableAB}
                            onStopAB={onStopAB}
                            abTestingLocked={abTestingLocked}
                            lockedTooltipContent={lockedTooltipContent}
                            renderForms={[
                                <BundleBuilderContentForm key="content" />,
                                <BundleBuilderDesignForm key="design" />,
                                <BundleBuilderTargetForm key="targeting" />,
                            ]}
                            renderPreview={
                                <div
                                    ref={previewRef}
                                    style={{ userSelect: inspectorMode ? "none" : "text" }}
                                    onClickCapture={handleInspectorClick}
                                    className={`${styles.previewContainer} ${inspectorMode ? styles.inspectorActive : ""}`}
                                >
                                    <BundleBuilderPreview
                                        appConfigContext={appConfigContext}
                                        bundleConfig={previewBundleConfig}
                                        previewType={1}
                                        // inspectMode={inspectorMode}
                                    />
                                </div>
                            }
                            variantSwitcher={
                                <VariantTabs
                                    activeTarget={editor.activeTarget}
                                    variants={editor.variants}
                                    abEnabled={editor.abEnabled}
                                    abConfig={(editor.form.getValues() as any)?.ab_config}
                                    onSwitchVariant={async (targetId) => {
                                        return await editor.switchVariant(targetId);
                                    }}
                                    abTestingLocked={abTestingLocked}
                                    onAddVariant={editor.addVariant}
                                    onRemoveVariant={async (variantId) => {
                                        const hadActive = editor.activeTarget === variantId;
                                        await editor.removeVariant(variantId);
                                        if (hadActive) {
                                            const baseValues = editor.form.getValues();
                                            editor.form.reset(baseValues);
                                        }
                                    }}
                                    onUpdateABConfig={editor.updateABConfig}
                                    validationErrorCount={Object.keys(editor.form.formState.errors).length}
                                    lockedTooltipContent={lockedTooltipContent}
                                    bundleType={BundleTypes.BUNDLE_BUILDER}
                                />
                            }
                            forAiBundle={false}
                            inspectorMode={inspectorMode}
                            setInspectorMode={setInspectorMode}
                            hasUnsavedChanges={editor.hasUnsavedChanges}
                        />
                    ) : (
                        <TemplateSelectionScreenBB makeClean={() => editor.form.reset(editor.form.getValues())} />
                    )}
                    <StopABTestModal
                        open={stopAbModalOpen}
                        onClose={() => setStopAbModalOpen(false)}
                        formId={getSnapshot().form_id || ""}
                        bundleType="bundle_builder"
                        isPublished={!!getBoolean(getSnapshot().status)}
                        onWinnerApplied={handleWinnerApplied}
                    />
                </VariantFieldContext.Provider>
            </FormProvider>
        </BundleBuilderContext.Provider>
    );
};

export default BundleBuilder;
